import type { Session, AuthChangeEvent } from '@supabase/supabase-js';

import { supabase } from '@/services/supabase';
import { db } from '@/services/storage/db';
import type { StorageAdapter } from '@/services/storage/storage-adapter';
import type { User } from '@/types/user';
import { validateRedirectPath } from '@/utils/redirect-validation';

export class AuthService {
  constructor(private readonly storage: StorageAdapter) {}

  async signUp(
    email: string,
    password: string,
    displayName: string,
    redirect: string | null = null,
  ): Promise<{ user: User; emailConfirmationRequired: boolean }> {
    // Build the emailRedirectTo URL. If a validated redirect path is present,
    // append it as ?redirect= so AuthCallbackPage can send the user there after
    // the email-confirmation click resolves (F12.1 Path 1 — closes the gap left
    // by F12 which only wired magic-link and Google OAuth).
    const validatedRedirect = redirect != null ? validateRedirectPath(redirect) : '/';
    const baseCallback = `${window.location.origin}/auth/callback`;
    const emailRedirectTo =
      validatedRedirect !== '/'
        ? `${baseCallback}?redirect=${encodeURIComponent(validatedRedirect)}`
        : baseCallback;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // Ensure confirmation emails redirect to /auth/callback, not the project's default Site URL.
        // Without this override, Supabase uses the dashboard "Site URL" which may point to /profile.
        emailRedirectTo,
      },
    });
    if (error) throw new Error(error.message);
    if (!data.user) throw new Error('Sign up succeeded but no user was returned');

    const user = await this.storage.createUser({
      id: data.user.id,
      email,
      displayName,
      defaultState: '',
      preferredUnits: 'imperial',
    });

    await this.storage.updateUserPreferences(user.id, {
      theme: 'system',
      notificationsEnabled: true,
      notificationLeadDays: 3,
    });

    // session is null when the Supabase project requires email confirmation.
    // Callers must check emailConfirmationRequired and NOT attempt sync until
    // the user clicks the confirmation link and a real session is established.
    return { user, emailConfirmationRequired: data.session === null };
  }

  async signIn(email: string, password: string): Promise<User> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(error.message);
    if (!data.user) throw new Error('Sign in succeeded but no user was returned');

    let user = await this.storage.getUserById(data.user.id);
    if (!user) {
      // First sign-in on this device — create local record
      user = await this.storage.createUser({
        id: data.user.id,
        email: data.user.email ?? email,
        displayName: data.user.email ?? email,
        defaultState: '',
        preferredUnits: 'imperial',
      });
    }

    return user;
  }

  async signInWithMagicLink(email: string, redirect: string | null = null): Promise<void> {
    // Build the emailRedirectTo URL. If a validated redirect path is present,
    // append it as ?redirect= so AuthCallbackPage can send the user there after
    // the magic-link click resolves.
    const validatedRedirect = redirect != null ? validateRedirectPath(redirect) : '/';
    const baseCallback = `${window.location.origin}/auth/callback`;
    const emailRedirectTo =
      validatedRedirect !== '/'
        ? `${baseCallback}?redirect=${encodeURIComponent(validatedRedirect)}`
        : baseCallback;

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Ensure magic-link clicks redirect to /auth/callback so the session
        // is established there before the user is sent to the dashboard.
        emailRedirectTo,
      },
    });
    if (error) throw new Error(error.message);
  }

  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw new Error(error.message);
  }

  async signInWithGoogle(redirect: string | null = null): Promise<void> {
    // Build the redirectTo URL. If a validated redirect path is present,
    // append it as ?redirect= so AuthCallbackPage can send the user there after
    // Google OAuth resolves.
    const validatedRedirect = redirect != null ? validateRedirectPath(redirect) : '/';
    const baseCallback = `${window.location.origin}/auth/callback`;
    const redirectTo =
      validatedRedirect !== '/'
        ? `${baseCallback}?redirect=${encodeURIComponent(validatedRedirect)}`
        : baseCallback;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
      },
    });
    if (error) throw new Error(error.message);
  }

  /**
   * Called on the /auth/callback route after OAuth redirect.
   * Supabase client extracts the session from the URL hash automatically.
   * We ensure a local Dexie user record exists for first-time OAuth sign-ins.
   */
  async handleOAuthCallback(): Promise<void> {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw new Error(error.message);
    if (data.session) {
      await this.ensureUserRecord(data.session.user);
    }
  }

  private async ensureUserRecord(supabaseUser: { id: string; email?: string; user_metadata?: { full_name?: string; name?: string } }): Promise<void> {
    const existing = await this.storage.getUserById(supabaseUser.id);
    if (!existing) {
      const displayName =
        supabaseUser.user_metadata?.full_name ??
        supabaseUser.user_metadata?.name ??
        supabaseUser.email?.split('@')[0] ??
        'Hunter';
      await this.storage.createUser({
        id: supabaseUser.id,
        email: supabaseUser.email ?? '',
        displayName,
        defaultState: '',
        preferredUnits: 'imperial',
      });
    }
  }

  async getCurrentSession(): Promise<Session | null> {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[AuthService] getCurrentSession:', error.message);
      return null;
    }
    return data.session;
  }

  onAuthStateChange(
    callback: (event: AuthChangeEvent, session: Session | null) => void,
  ): () => void {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(callback);
    return () => subscription.unsubscribe();
  }
}

export const authService = new AuthService(db);
